import type { ReferenceType, Shipment } from "api";
import { PENDING_TYPE } from "api";
import { AIR_TRACKING_URL, TRACKING_URL } from "constants/common.constants";
import { formatShortDate } from "utils/date.utils";
import { createAirlineIconUrl, createCarrierIconUrl } from "utils/url.utils";

/**
 * The buckets the design's filter bar offers. A shipment with no arrival date
 * yet belongs to none of them and shows only under "All" — the same way the
 * prototype behaves.
 */
export const SHIPMENT_BUCKETS = ["soon", "week", "late", "done"] as const;

export type ShipmentBucket = (typeof SHIPMENT_BUCKETS)[number];

export type BucketFilter = "all" | ShipmentBucket;

/**
 * The design's mode bar also offers road, rail and multimodal. The back office
 * stores `shipment_type` as an enum of exactly SEA and AIR (`App\Enums\
 * ShipmentType`), and nothing else can reach the column, so those three pills
 * would filter every row away. They stay out until the API can report them.
 */
export type ModeFilter = "all" | "ocean" | "air";

export const BUCKET_LABELS: Record<BucketFilter, string> = {
  all: "All",
  soon: "Arriving in ≤ 3 days",
  week: "In 4–7 days",
  late: "Delayed",
  done: "Delivered",
};

export const MODE_LABELS: Record<ModeFilter, string> = {
  all: "All",
  ocean: "Sea",
  air: "Air",
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** "IN_TRANSIT" reads as "In transit" — the API sends screaming snake case. */
const readableStatus = (status: string | null): string => {
  if (!status) return "Unknown";

  return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ");
};

const parse = (value: string | null): number | null => {
  if (!value) return null;

  const time = new Date(value).getTime();

  return Number.isNaN(time) ? null : time;
};

/**
 * Whole days from today to `value`, negative once it is in the past. Both ends
 * are floored to a day so "arrives in 2 days" does not flip on the clock.
 */
export const daysUntil = (value: string | null): number | null => {
  const time = parse(value);

  if (time === null) return null;

  return Math.floor(time / DAY_MS) - Math.floor(Date.now() / DAY_MS);
};

/**
 * The arrival date to reckon with. The widget writes the carrier's date into
 * `to` and its own forecast into `to_predictive`, never both, so whichever is
 * set is the only estimate there is.
 */
export const arrivalOf = (shipment: Shipment): string | null => shipment.date.to ?? shipment.date.to_predictive;

export const isDelivered = (shipment: Shipment): boolean => shipment.status === "DELIVERED" || shipment.date.to_actual;

export const bucketOf = (shipment: Shipment): ShipmentBucket | null => {
  if (isDelivered(shipment)) return "done";

  const days = daysUntil(arrivalOf(shipment));

  if (days === null) return null;
  // Past its arrival date and still not delivered — that is the delay signal.
  if (days < 0) return "late";
  if (days <= 3) return "soon";
  if (days <= 7) return "week";

  return null;
};

const modeOf = (shipment: Shipment): ModeFilter => (shipment.shipment_type === "AIR" ? "air" : "ocean");

export type ModeBadge = {
  label: string;
  /** `pcsh__mode` modifier — it is what colours the badge. */
  modifier: string;
};

/** The mode alone: its name, and the modifier that colours the badge. */
export const modeBadge = (shipment: Shipment): ModeBadge =>
  shipment.shipment_type === "AIR"
    ? { label: "Air", modifier: "pcsh__mode--air" }
    : { label: "Sea", modifier: "pcsh__mode--sea" };

/**
 * The badge under the carrier logo, as the design prints it: the load type,
 * coloured by the mode. A card nobody has opened on the Cargo tab has no load
 * type yet; it reads FCL, which is what that tab opens on — so the list and the
 * shipment's own page never disagree about the same card.
 */
export const loadBadge = (shipment: Shipment): ModeBadge => ({
  ...modeBadge(shipment),
  label: shipment.load_type ?? "FCL",
});

export const matchesFilters = (shipment: Shipment, bucket: BucketFilter, mode: ModeFilter): boolean => {
  if (mode !== "all" && modeOf(shipment) !== mode) return false;

  return bucket === "all" || bucketOf(shipment) === bucket;
};

export type StatusTag = {
  label: string;
  /** `pc-tag` modifier, or null for the plain tag. */
  modifier: string | null;
};

/**
 * The headline in the status cell: when it arrives, or that it already has.
 * Counting in days is what the design shows; the exact date is the fallback
 * once "in N days" stops being useful.
 */
export const statusTag = (shipment: Shipment): StatusTag => {
  // Nothing is being tracked yet, so no arrival can be promised — the card is
  // a placeholder until the carrier issues the number.
  if (isPending(shipment)) return { label: "No number yet", modifier: "pc-tag--new" };

  if (isDelivered(shipment)) {
    const date = formatShortDate(shipment.date.to);

    return { label: date ? `Delivered ${date}` : "Delivered", modifier: "pc-tag--mute" };
  }

  const eta = arrivalOf(shipment);
  const days = daysUntil(eta);

  if (days === null) {
    return { label: "Awaiting first event", modifier: "pc-tag--new" };
  }

  if (days < 0) return { label: `Overdue since ${formatShortDate(eta)}`, modifier: "pc-tag--bad" };

  const modifier = days <= 3 ? "pc-tag--ok" : "pc-tag--info";

  if (days === 0) return { label: "Arrives today", modifier };
  if (days === 1) return { label: "Arrives tomorrow", modifier };
  if (days <= 7) return { label: `Arrives in ${days} days`, modifier };

  return { label: `Arrives ${formatShortDate(eta)}`, modifier };
};

/**
 * The grey line under the schedule: how far off that date is. The date itself
 * is the headline, so this only has to say "soon" or "late" in words.
 */
export const relativeArrivalLabel = (shipment: Shipment): string => {
  if (isPending(shipment)) return "No date yet";
  if (isDelivered(shipment)) return "Delivered";

  const days = daysUntil(arrivalOf(shipment));

  if (days === null) return "Date to be confirmed";
  if (days < 0) return `${-days} ${-days === 1 ? "day" : "days"} late`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";

  return `In ${days} days`;
};

/** The grey second line under the status: what it is doing, and where. */
export const statusDetail = (shipment: Shipment): string => {
  const where = routeOf(shipment);
  const state = isPending(shipment) ? "Tracking starts once the number is known" : readableStatus(shipment.status);

  return where ? `${state} · ${where}` : state;
};

/** "Shanghai CN → Rotterdam NL", or empty when neither end is known. */
export const routeOf = (shipment: Shipment): string =>
  [shipment.location.from, shipment.location.to].filter(Boolean).join(" → ");

/**
 * Saved before the shipment had a number. Its `reference` is a placeholder the
 * back office issued to keep the card addressable, not a number any carrier
 * would recognise — so it names no column and opens no tracking page.
 */
export const isPending = (shipment: Shipment): boolean => shipment.type === PENDING_TYPE;

/**
 * The date band in the booking cell. `date.to`/`date.from` carry both the
 * estimate and the actual, and the `*_actual` flags say which one it is.
 */
export const scheduleLabel = (shipment: Shipment): string => {
  const { from, from_actual: fromActual, to, to_actual: toActual, to_predictive: predicted } = shipment.date;

  if (toActual && to) return `${shipment.status === "DELIVERED" ? "Delivered" : "ATA"} ${formatShortDate(to)}`;
  if (to) return `ETA ${formatShortDate(to)}`;
  if (predicted) return `ETA ${formatShortDate(predicted)}`;
  if (fromActual && from) return `ATD ${formatShortDate(from)}`;
  if (from) return `ETD ${formatShortDate(from)}`;

  return "—";
};

/**
 * The reference only ever fills the column its own type names — a card tracked
 * by container number carries no booking number to show, and inventing one
 * would be worse than the dash.
 */
export const referenceFor = (shipment: Shipment, column: ReferenceType): string =>
  shipment.type === column ? shipment.reference : "—";

export const carrierLabel = (shipment: Shipment): string =>
  shipment.carrier_name ?? shipment.carrier_scac ?? "Carrier pending";

/** Stands in for a carrier that is not known yet, or has no logo in the icon set. */
export const CARRIER_CUBE = "/images/pages/cabinet/green.webp";

/**
 * The carrier's logo from the apps origin's icon sets: sea lines by SCAC,
 * airlines by IATA code. The sets do not cover every carrier, so the image
 * that renders it still falls back to `CARRIER_CUBE` when the file is missing.
 */
export const carrierLogoUrl = (shipment: Shipment): string => {
  const code = shipment.carrier_scac?.trim();

  if (!code) return CARRIER_CUBE;

  return shipment.shipment_type === "AIR" ? createAirlineIconUrl(code) : createCarrierIconUrl(code);
};

export type RouteStop = { name: string; done: boolean };

/**
 * `.shipp__route`: the first stop reached, the last still ahead. The design
 * marks a middle stop as current, but a card stores only its two ends; an end
 * the carrier has not named yet keeps the design's placeholder.
 */
export const routeStops = (shipment: Shipment): RouteStop[] => [
  { name: shipment.location.from ?? "Origin", done: true },
  { name: shipment.location.to ?? "Destination", done: false },
];

/** The route strip's `data-mode`, which colours its dots and line. */
export const routeMode = (shipment: Shipment): "air" | "ocean" => (shipment.shipment_type === "AIR" ? "air" : "ocean");

/**
 * The tracking tool, opened on this shipment. It reads the same query the
 * gateway search takes, so the card opens with its route and events already
 * drawn — which is why the cabinet does not carry a map of its own.
 *
 * Null when there is no number to open it on.
 */
export const trackingUrl = (shipment: Shipment): string | null => {
  if (isPending(shipment)) return null;

  if (shipment.shipment_type === "AIR") {
    return `${AIR_TRACKING_URL}?awb_number=${encodeURIComponent(shipment.reference)}`;
  }

  const query = new URLSearchParams({ reference_number: shipment.reference });

  if (shipment.type !== null) query.set("reference_type", shipment.type);
  if (shipment.carrier_scac !== null) query.set("carrier_code", shipment.carrier_scac);

  return `${TRACKING_URL}?${query.toString()}`;
};

/** Human-facing id for a saved card, as the design's `pcsh__sid` shows it. */
export const shipmentCode = (shipment: Shipment): string => `QT-${shipment.id}`;

/**
 * `size_type` arrives as a JSON array of already-counted container types —
 * the tracking widget writes entries like "3 x 40HC" — so the label only has
 * to join them back together.
 */
export const equipmentLabel = (shipment: Shipment): string => {
  const fallback = shipment.shipment_type === "AIR" ? "air freight" : "type not specified";
  const raw = shipment.size_type;

  if (!raw) return fallback;

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    // Older cards stored a bare type rather than a list.
    return raw;
  }

  if (!Array.isArray(parsed)) return raw;

  const types = parsed.filter((value): value is string => typeof value === "string" && value !== "");

  return types.length === 0 ? fallback : types.join(" · ");
};
